import React, { memo, useCallback, useRef, useState } from 'react';
import * as Yup from 'yup';
import { UploadOutlined } from '@ant-design/icons';
import { Col, Input, InputRef, Row, Select, Typography } from 'antd';

import { ILandmark } from 'store/slices/openingInfo/interfaces';
import { IDocument } from 'types/document';

import { CustomUpload } from '../CustomUpload/CustomUpload';
import { CustomCollapse } from './components/CustomCollapse/CustomCollapse';
import { quarters } from './constants';
import { getYears } from './utils';
import styles from './Form.module.less';

interface IProps {
  onDelete: (id: string) => void;
  onMove: (entity: ILandmark, step?: number) => void;
  onUpdate: (
    file: IDocument,
    title: string,
    quart: number,
    year: number,
    entity: ILandmark
  ) => void;
  entity: ILandmark;
  total: number;
}

export const FormWithSelect = memo(
  ({ onUpdate, onDelete, onMove, entity, total }: IProps) => {
    const inputRef = useRef<InputRef>(null);
    const [title, setTitle] = useState<string>(entity.title);
    const [file, setFile] = useState<IDocument>(entity.file);
    const [quart, setQuart] = useState<number>(entity.quart);
    const [year, setYear] = useState<number>(entity.year);
    const [error, setError] = useState<string>(null);

    const handleBlur = () =>
      !error && onUpdate(file, title, quart, year, entity);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      const isValid = Yup.string().required().isValidSync(value);
      if (!isValid) {
        setError('Введите от 1 до 255 символов');
      } else {
        setError(null);
      }
      setTitle(e.target.value);
    };

    const handleChangeQuart = (value: number) => setQuart(value);

    const handleChangeYear = (value: number) => setYear(value);

    const handleChangeState = useCallback((file: IDocument) => {
      const title = file.realName.split('.').slice(0, -1).join('.');
      setTitle(title);
      setFile(file);
      inputRef.current.focus();
    }, []);

    return (
      <CustomCollapse
        onMove={step => onMove(entity, step)}
        onDelete={onDelete}
        title={title}
        file={file}
        itemId={entity.id}
        total={total}
        sortIndex={entity.sortIndex}
        isError={!!error}
      >
        <Row gutter={[24, 24]}>
          <Col flex={1}>
            <Input
              ref={inputRef}
              className={styles.input}
              placeholder='Название документа'
              type='text'
              value={title}
              onChange={handleChange}
              onBlur={handleBlur}
              minLength={1}
              maxLength={255}
              status={error ? 'error' : undefined}
              addonBefore={<span className={styles.required} />}
            />
            {error && <Typography.Text type='danger'>{error}</Typography.Text>}
          </Col>
          <Col>
            <CustomUpload
              onAdd={handleChangeState}
              icon={<UploadOutlined />}
              buttonTitle='Загрузить другой'
            />
          </Col>
        </Row>
        <Row gutter={[24, 24]} className={styles.datePickerRow}>
          <Col span={24} md={10}>
            <div className={styles.select}>
              <label htmlFor={`quart-${entity.id}`}>Квартал</label>
              <Select
                id={`quart-${entity.id}`}
                value={quart}
                onChange={handleChangeQuart}
                onBlur={handleBlur}
                options={quarters}
              />
            </div>
          </Col>
          <Col span={24} md={10}>
            <div className={styles.select}>
              <label htmlFor={`year-${entity.id}`}>Год</label>
              <Select
                id={`year-${entity.id}`}
                value={year}
                onChange={handleChangeYear}
                onBlur={handleBlur}
                options={getYears()}
              />
            </div>
          </Col>
        </Row>
      </CustomCollapse>
    );
  }
);

FormWithSelect.displayName = 'FormWithSelect';
